import React, { useState, useEffect } from 'react';
import { FiArrowLeft, FiArrowRight, FiCheck, FiX } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import Tooltip from '../../../components/tooltip';
import { submitCompliance, ComplianceType, saveComplianceResult, generateConversationId } from '../../../services/compliance';
import Loading from '../../../components/Loading';
import {
  Container,
  Header,
  BackButton,
  TitleContainer,
  Title,
  Subtitle,
  FormTitle,
  FormDescription,
  FormContainer,
  Form,
  FormGroup,
  Label,
  Input,
  ButtonContainer,
  SubmitButton,
  RequiredFieldsNote,
  TimelineContainer,
  TimelineProgress,
  TimelineBackground,
  StepItem,
  StepCircle,
  StepLabel,
  FormSection,
  SectionTitle,
  SectionDescription,
  NavigationButtons,
  PrevButton,
  NextButton,
  SectionStatus,
  StatusText,
  ProgressBar,
  ProgressFill,
  Checkbox,
  CheckboxLabel,
  CheckboxContainer,
  ConditionalField,
  RadioGroup,
  RadioOption,
  Radio,
  RadioLabel,
  CheckboxGroupHorizontal,
  CheckboxWithLabel,
  Obrigatorio,
} from './styles';
import { Toast as ToastContainer } from '../pending/styles';

// Interface para o estado de navegação
interface NavigationState {
  showModal: boolean;
  modalData: any;
  completed: boolean;
  resultContent: string;
  apiResponse: any;
  fields: any;
  dataHasBeenSaved: boolean;
  isNewSubmission?: boolean;
}

// Estrutura para cada seção
interface Section {
  id: number;
  title: string;
  description: string;
  fields: Field[];
}

// Estrutura para cada campo
interface Field {
  id: string;
  label: string;
  type: string;
  required: boolean;
}

// Estrutura para os valores do formulário
interface FormValues {
  [key: string]: string;
}

// Estrutura para seções com problemas
interface ProblemSection {
  index: number;
  title: string;
}

// Interface para props do FormPage
interface FormPageProps {
  initialValues?: FormValues;
  initialSection?: number;
}

export const FormPage: React.FC<FormPageProps> = ({ initialValues = {}, initialSection = 0 }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Função para converter datas para o formato YYYY-MM-DD
  const normalizeDate = (dateStr: string): string => {
    if (!dateStr) return '';
    
    // Se já estiver no formato ISO, retornar como está
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    
    // Se for apenas o ano (ex: "2020"), adicionar o mês e dia atuais
    if (/^\d{4}$/.test(dateStr)) {
      const currentDate = new Date();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      return `${dateStr}-${month}-${day}`;
    }
    
    return dateStr;
  };
  
  // Normalizar valores iniciais de datas
  const normalizedInitialValues = { ...initialValues };
  if (normalizedInitialValues.saleYear && normalizedInitialValues.saleYear !== 'no') {
    normalizedInitialValues.saleYear = normalizeDate(normalizedInitialValues.saleYear);
  }
  
  // Inicializar os estados com os valores normalizados
  const [currentSection, setCurrentSection] = useState<number>(initialSection);
  const [formValues, setFormValues] = useState<FormValues>(normalizedInitialValues);
  const [sectionProgress, setSectionProgress] = useState<number[]>(Array(7).fill(0));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Obter dados do state quando o componente é montado via navegação
  useEffect(() => {
    console.log("FormPage - Verificando dados iniciais");
    
    if (!formInitialized) {
      // Verificar se há dados no localStorage
      const storedFormData = localStorage.getItem('formData');
      const storedInitialSection = localStorage.getItem('initialSection');
      
      if (storedFormData) {
        try {
          console.log("Encontrados dados no localStorage:", storedFormData);
          const parsedData = JSON.parse(storedFormData);
          setFormValues(parsedData);
          
          // Limpar do localStorage após uso
          localStorage.removeItem('formData');
        } catch (error) {
          console.error("Erro ao processar dados do localStorage:", error);
        }
      }
      
      if (storedInitialSection) {
        try {
          console.log("Encontrada seção inicial no localStorage:", storedInitialSection);
          const sectionNumber = parseInt(storedInitialSection);
          setCurrentSection(sectionNumber);
          
          // Limpar do localStorage após uso
          localStorage.removeItem('initialSection');
        } catch (error) {
          console.error("Erro ao processar seção inicial do localStorage:", error);
        }
      }
      
      // Se não encontrou no localStorage, tentar location.state
      else if (location.state) {
        const state: any = location.state;
        console.log("Estado recebido no FormPage:", state);
        
        if (state.initialValues) {
          console.log("Valores iniciais obtidos do state:", state.initialValues);
          setFormValues(state.initialValues);
        }
        
        if (state.initialSection !== undefined) {
          console.log("Seção inicial obtida do state:", state.initialSection);
          setCurrentSection(state.initialSection);
        }
      } else {
        console.log("Nenhum dado encontrado no localStorage ou location.state");
      }
      
      setFormInitialized(true);
    }
  }, [location.state, formInitialized]);

  // Depurar valores do formulário após cada renderização
  useEffect(() => {
    console.log("FormValues atualizados:", formValues);
    console.log("Seção atual:", currentSection);
  }, [formValues, currentSection]);

  // Helper para renderizar ícones corretamente
  const renderIcon = (Icon: any, props: any) => {
    return <Icon {...props} />;
  };

  // Definição das 7 seções
  const sections: Section[] = [
    {
      id: 1,
      title: "Personal Information",
      description: "Basic information for identification",
      fields: [
        { id: "fullName", label: "Full Name", type: "text", required: false },
        { id: "birthDate", label: "Date of Birth", type: "date", required: false },
        { id: "nationality", label: "Nationality(default: Brazilian)", type: "text", required: false }
      ]
    },
    {
      id: 2,
      title: "Education",
      description: "This section is only required if the customer has completed a college or university program within the past 10 years.",
      fields: [
        { id: "hasCompletedCollege", label: "Have you completed a college or university program in the last 10 years?", type: "radio", required: false },
        { id: "institution", label: "Name of institutions attended", type: "text", required: false },
        { id: "degree", label: "Degrees obtained", type: "text", required: false },
        { id: "fieldsOfStudy", label: "Fields of study", type: "text", required: false },
        { id: "datesOfCompletion", label: "Dates of completion (if known)", type: "date", required: false },
        { id: "relevantContinuingEducation", label: "Any relevant continuing education or postgraduate certifications", type: "text", required: false },
      ]
    },
    {
      id: 3,
      title: "Career History",
      description: "Please provide a free-form summary of your career over the past 10 years.",
      fields: [
        { id: "jobTitles", label: "Job titles", type: "text", required: false },
        { id: "employers", label: "Employers", type: "text", required: false },
        { id: "durationInEachRole", label: "Duration in each role", type: "text", required: false },
        { id: "descriptionOfResponsibilities", label: "Description of responsibilities and notable milestones", type: "text", required: false },
        { id: "anySignificantCareer", label: "Any significant career changes or gaps", type: "text", required: false },
      ]
    },
    {
      id: 4,
      title: "Source of Wealth",
      description: "Describe in detail how accumulated their wealth.",
      fields: [
        { id: "professionalIncome", label: "Professional income", type: "number", required: false },
        { id: "ownsBusinessOwnership", label: "Owns business ownership?*", type: "radio", required: false },
        { id: "entrepreneur", label: "Do you consider yourself an entrepreneur or consultant?", type: "text", required: false },
        { id: "sectorActivity", label: "If yes, what sector and activity?", type: "text", required: false },
        { id: "trader", label: "Are you a trader?", type: "text", required: false },
        { id: "traderType", label: "do you trade on your own (proprietary trading) or through a brokerage (brokerage-based)?", type: "text", required: false },
        { id: "hasInvestmentIncome", label: "Has investment income?", type: "text", required: false },
        { id: "investmentTypes", label: "If yes, what types of investments do you have?", type: "checkbox", required: false },
        { id: "hasInheritedAssets", label: "Has inherited assets?", type: "radio", required: false },
        { id: "inheritanceDetails", label: "Inheritance details", type: "group", required: false },
        { id: "soldCompanyProperty", label: "Sold a company or property?", type: "radio", required: false },
        { id: "saleDetails", label: "Sale details", type: "group", required: false },
        { id: "hasGovernmentContracts", label: "Has government contracts?", type: "radio", required: false },
        { id: "governmentContractDetails", label: "Government contract details", type: "group", required: false },
        { id: "worksWithAgrobusiness", label: "Works with agrobusiness?", type: "radio", required: false },
        { id: "agrobusinessDetails", label: "Agrobusiness details", type: "group", required: false }
      ]
    },
    {
      id: 5,
      title: "Financial Information",
      description: "",
      fields: [
        { id: "totalNetWorth", label: "Total Net Worth (in USD)", type: "number", required: false },
        { id: "liquidNetWorth", label: "Liquid Net Worth (in USD)", type: "number", required: false },
        { id: "expectedAccountSize", label: "Expected Account Size (in USD, if known)", type: "number", required: false },
        { id: "otherWealthSources", label: "Are there other sources of wealth not mentioned above?", type: "text", required: false }
      ]
    },
    {
      id: 6,
      title: "Politically Exposed Person (PEP) Status",
      description: "",
      fields: [
        { id: "holdsPoliticalOffice", label: "Holds or held a prominent public office?", type: "radio", required: false },
        { id: "politicalOfficeDetails", label: "Political office details", type: "checkbox-group", required: false },
        { id: "hasRelationshipWithPEP", label: "Have a close relationship with someone who fits the profile above?", type: "radio", required: false },
        { id: "pepRelationshipDetails", label: "PEP relationship details", type: "checkbox-group", required: false },
        { id: "hasGovernmentPosition", label: "Have you ever held a government, judicial, military or political position, or worked in a state-owned company?", type: "radio", required: false },
        { id: "governmentPositionDetails", label: "Government position details", type: "group", required: false }
      ]
    }
  ];

  // Atualizar progresso da seção quando os valores do formulário mudarem
  useEffect(() => {
    updateSectionProgress();
  }, [formValues]);

  // Calcular o progresso de cada seção
  const updateSectionProgress = () => {
    const newProgress = sections.map((section, index) => {
      // Considerar TODOS os campos da seção, aplicando lógica condicional
      const relevantFields = section.fields.filter(field => {
        // Para os campos de educação, só são relevantes se "Has completed college" for "yes"
        if ((field.id === 'institution' || field.id === 'degree' || field.id === 'fieldsOfStudy' || 
             field.id === 'datesOfCompletion' || field.id === 'relevantContinuingEducation')) {
          return formValues.hasCompletedCollege === 'yes';
        }
        
        // Para o campo condicional de setor/atividade, só é relevante se a opção "Sim" estiver selecionada
        if (field.id === 'sectorActivity') {
          return formValues.isEntrepreneur === 'yes';
        }
        // Para o campo condicional de tipo de trader, só é relevante se a opção "Sim" estiver selecionada para trader
        if (field.id === 'traderType') {
          return formValues.operateFinancial === 'yes';
        }
        // Para o campo condicional de tipos de investimento, só é relevante se a opção "Sim" estiver selecionada
        if (field.id === 'investmentTypes') {
          return formValues.hasInvestmentIncome === 'yes';
        }
        // Para os campos de herança, só são relevantes se "Has inherited assets" for "sim"
        if (field.id === 'inheritanceDetails') {
          return formValues.hasInheritedAssets === 'yes';
        }
        // Para os campos de venda, só são relevantes se "Sold a company or property" for "sim"
        if (field.id === 'saleDetails') {
          return formValues.soldCompanyProperty === 'yes';
        }
        // Para os campos de contrato governamental, só são relevantes se "Has government contracts" for "sim"
        if (field.id === 'governmentContractDetails') {
          return formValues.hasGovernmentContracts === 'yes';
        }
        // Para os campos de agronegócio, só são relevantes se "Works with agrobusiness" for "sim"
        if (field.id === 'agrobusinessDetails') {
          return formValues.worksWithAgrobusiness === 'yes';
        }
        // Para os campos de cargo governamental, só são relevantes se "Has government position" for "sim"
        if (field.id === 'governmentPositionDetails') {
          return formValues.hasGovernmentPosition === 'yes';
        }
        // Para os campos de relacionamento com PEP, só são relevantes se "Has relationship with PEP" for "sim"
        if (field.id === 'pepRelationshipDetails') {
          return formValues.hasRelationshipWithPEP === 'yes';
        }
        // Para os campos de cargo político, só são relevantes se "Holds political office" for "sim"
        if (field.id === 'politicalOfficeDetails') {
          return formValues.holdsPoliticalOffice === 'yes';
        }
        
        // Para todos os outros campos, sempre considerar como relevante
        return true;
      });
      
      if (relevantFields.length === 0) return 100;
      
      // Verificar campos preenchidos, considerando a lógica condicional
      const fieldsCompleted = relevantFields.filter(field => {
        // Para o campo hasCompletedCollege
        if (field.id === 'hasCompletedCollege') {
          return formValues.hasCompletedCollege === 'yes' || formValues.hasCompletedCollege === 'no';
        }
        
        // Para os campos de educação (só são relevantes se "Has completed college" for "yes")
        if ((field.id === 'institution' || field.id === 'degree') && formValues.hasCompletedCollege === 'yes') {
          return formValues[field.id] && formValues[field.id].trim() !== '';
        }
        
        // Para campos de educação opcionais (fieldsOfStudy, datesOfCompletion, relevantContinuingEducation)
        if ((field.id === 'fieldsOfStudy' || field.id === 'datesOfCompletion' || field.id === 'relevantContinuingEducation') && formValues.hasCompletedCollege === 'yes') {
          return formValues[field.id] && formValues[field.id].trim() !== '';
        }
        
        // Para o radio de empreendedor/consultor
        if (field.id === 'entrepreneur') {
          return formValues.isEntrepreneur === 'yes' || formValues.isEntrepreneur === 'no';
        }
        
        // Para o radio de operar no mercado financeiro
        if (field.id === 'trader') {
          return formValues.operateFinancial === 'yes' || formValues.operateFinancial === 'no';
        }
        
        // Para o radio de renda de investimentos
        if (field.id === 'hasInvestmentIncome') {
          return formValues.hasInvestmentIncome === 'yes' || formValues.hasInvestmentIncome === 'no';
        }
        
        // Para o radio de ativos herdados
        if (field.id === 'hasInheritedAssets') {
          return formValues.hasInheritedAssets === 'yes' || formValues.hasInheritedAssets === 'no';
        }
        
        // Para o radio de venda de empresa/propriedade
        if (field.id === 'soldCompanyProperty') {
          return formValues.soldCompanyProperty === 'yes' || formValues.soldCompanyProperty === 'no';
        }
        
        // Para o radio de contratos governamentais
        if (field.id === 'hasGovernmentContracts') {
          return formValues.hasGovernmentContracts === 'yes' || formValues.hasGovernmentContracts === 'no';
        }
        
        // Para o radio de agronegócio
        if (field.id === 'worksWithAgrobusiness') {
          return formValues.worksWithAgrobusiness === 'yes' || formValues.worksWithAgrobusiness === 'no';
        }
        
        // Para o radio de propriedade de negócios
        if (field.id === 'ownsBusinessOwnership') {
          return formValues.ownsBusinessOwnership === 'yes' || formValues.ownsBusinessOwnership === 'no';
        }
        
        // Para o radio de tipo de trader (só relevante se for trader)
        if (field.id === 'traderType' && formValues.operateFinancial === 'yes') {
          return formValues.traderType === 'proprietary' || formValues.traderType === 'brokerage';
        }
        
        // Para o campo condicional de setor/atividade
        if (field.id === 'sectorActivity' && formValues.isEntrepreneur === 'yes') {
          return formValues.sectorActivity && (typeof formValues.sectorActivity === 'string' ? formValues.sectorActivity.trim() !== '' : formValues.sectorActivity !== '');
        }
        
        // Para o campo condicional de tipos de investimento (pelo menos um checkbox deve estar marcado)
        if (field.id === 'investmentTypes' && formValues.hasInvestmentIncome === 'yes') {
          return formValues.realEstateInvestment === 'yes' || 
                 formValues.privateEquityInvestment === 'yes' || 
                 formValues.otherInvestment === 'yes';
        }
        
        // Para o campo de tipos de investimento - caso especial quando hasInvestmentIncome é "no"
        if (field.id === 'investmentTypes' && formValues.hasInvestmentIncome === 'no') {
          return true; // Sempre válido quando hasInvestmentIncome é "no", pois otherInvestment será automaticamente "yes"
        }
        
        // Para os campos de herança (devem estar preenchidos se "Has inherited assets" for "sim")
        if (field.id === 'inheritanceDetails' && formValues.hasInheritedAssets === 'yes') {
          return formValues.valueInherited && 
                 formValues.relationshipToDeceased && (typeof formValues.relationshipToDeceased === 'string' ? formValues.relationshipToDeceased.trim() !== '' : formValues.relationshipToDeceased !== '') &&
                 formValues.nameOfDeceased && (typeof formValues.nameOfDeceased === 'string' ? formValues.nameOfDeceased.trim() !== '' : formValues.nameOfDeceased !== '');
        }
        
        // Para os campos de venda (devem estar preenchidos se "Sold a company or property" for "sim")
        if (field.id === 'saleDetails' && formValues.soldCompanyProperty === 'yes') {
          return formValues.saleYear && formValues.saleValue;
        }
        
        // Para os campos de contratos governamentais (devem estar preenchidos se "Has government contracts" for "sim")
        if (field.id === 'governmentContractDetails' && formValues.hasGovernmentContracts === 'yes') {
          return formValues.contractType && (typeof formValues.contractType === 'string' ? formValues.contractType.trim() !== '' : formValues.contractType !== '') &&
                 formValues.publicEntityInvolved && (typeof formValues.publicEntityInvolved === 'string' ? formValues.publicEntityInvolved.trim() !== '' : formValues.publicEntityInvolved !== '') &&
                 formValues.customerRole && (typeof formValues.customerRole === 'string' ? formValues.customerRole.trim() !== '' : formValues.customerRole !== '');
        }
        
        // Para os campos de agronegócio (devem estar preenchidos se "Works with agrobusiness" for "sim")
        if (field.id === 'agrobusinessDetails' && formValues.worksWithAgrobusiness === 'yes') {
          // Não há mais campos para validar aqui, então sempre retornamos verdadeiro
          return true;
        }
        
        // Para o radio de cargo político
        if (field.id === 'holdsPoliticalOffice') {
          return formValues.holdsPoliticalOffice === 'yes' || formValues.holdsPoliticalOffice === 'no';
        }
        
        // Para o radio de relacionamento com PEP
        if (field.id === 'hasRelationshipWithPEP') {
          return formValues.hasRelationshipWithPEP === 'yes' || formValues.hasRelationshipWithPEP === 'no';
        }
        
        // Para o radio de cargo governamental
        if (field.id === 'hasGovernmentPosition') {
          return formValues.hasGovernmentPosition === 'yes' || formValues.hasGovernmentPosition === 'no';
        }
        
        // Para os campos de cargo governamental (todos os campos devem estar preenchidos)
        if (field.id === 'governmentPositionDetails' && formValues.hasGovernmentPosition === 'yes') {
          return formValues.positionTitle && (typeof formValues.positionTitle === 'string' ? formValues.positionTitle.trim() !== '' : formValues.positionTitle !== '') &&
                 formValues.publicFigureName && (typeof formValues.publicFigureName === 'string' ? formValues.publicFigureName.trim() !== '' : formValues.publicFigureName !== '') &&
                 formValues.organizationName && (typeof formValues.organizationName === 'string' ? formValues.organizationName.trim() !== '' : formValues.organizationName !== '') &&
                 formValues.countryJurisdiction && (typeof formValues.countryJurisdiction === 'string' ? formValues.countryJurisdiction.trim() !== '' : formValues.countryJurisdiction !== '') &&
                 formValues.positionStartDate &&
                 formValues.positionEndDate;
        }
        
        // Para os campos de relacionamento com PEP (pelo menos uma opção deve estar marcada)
        if (field.id === 'pepRelationshipDetails' && formValues.hasRelationshipWithPEP === 'yes') {
          return formValues.isSpousePartner === 'yes' || 
                 formValues.isFamily === 'yes' || 
                 formValues.isBusinessAssociate === 'yes';
        }

        // Para os campos de cargo político (pelo menos uma opção deve estar marcada)
        if (field.id === 'politicalOfficeDetails' && formValues.holdsPoliticalOffice === 'yes') {
          return formValues.isGovernmentOfficial === 'yes' || 
                 formValues.isJudge === 'yes' || 
                 formValues.isMilitaryOfficer === 'yes' ||
                 formValues.isStateCompanyExecutive === 'yes' ||
                 formValues.isPoliticalPartyLeader === 'yes';
        }
        
        // Para outros campos de texto/número/data, verificar se estão preenchidos
        const fieldValue = formValues[field.id];
        if (fieldValue !== undefined && fieldValue !== null) {
          const stringValue = fieldValue.toString();
          const isValid = typeof stringValue === 'string' ? stringValue.trim() !== '' : stringValue !== '';
          return isValid;
        } else {
          return false;
        }
      });
      
      return Math.round((fieldsCompleted.length / relevantFields.length) * 100);
    });
    
    setSectionProgress(newProgress);
  };

  const handleBack = () => {
    navigate('/compliance');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    // Para datas, especialmente para o campo saleYear, usar a função normalizeDate
    if (name === 'saleYear') {
      const normalizedValue = normalizeDate(value);
      setFormValues(prev => ({
        ...prev,
        [name]: normalizedValue
      }));
      return;
    }
    
    if (type === 'radio') {
      setFormValues(prev => ({
        ...prev,
        [name]: value
      }));

      // Se a resposta for "no" para empreendedor/consultor, limpar o campo de setor/atividade
      if (name === 'isEntrepreneur' && value === 'no') {
        setFormValues(prev => ({
          ...prev,
          sectorActivity: 'no'
        }));
      }
      
      // Se a resposta for "no" para "Have you completed a college?", limpar os campos de educação
      if (name === 'hasCompletedCollege' && value === 'no') {
        setFormValues(prev => ({
          ...prev,
          institution: 'no',
          degree: 'no',
          fieldsOfStudy: 'no',
          datesOfCompletion: 'no',
          relevantContinuingEducation: 'no'
        }));
      }
      
      // Se a resposta for "no" para "Are you a trader?", limpar a resposta de tipo de trader
      if (name === 'operateFinancial' && value === 'no') {
        setFormValues(prev => ({
          ...prev,
          traderType: 'no'
        }));
      }
      
      // Se a resposta for "no" para "Has investment income?", limpar a seleção de tipos de investimento
      if (name === 'hasInvestmentIncome' && value === 'no') {
        setFormValues(prev => ({
          ...prev,
          realEstateInvestment: 'no',
          privateEquityInvestment: 'no',
          otherInvestment: 'yes'  // Marcado como 'yes' quando hasInvestmentIncome é 'no'
        }));
      } else if (name === 'hasInvestmentIncome' && value === 'yes') {
        // Quando o usuário seleciona "yes" para hasInvestmentIncome, 
        // garantir que todos os checkboxes comecem com o valor "no"
        setFormValues(prev => ({
          ...prev,
          realEstateInvestment: prev.realEstateInvestment || 'no',
          privateEquityInvestment: prev.privateEquityInvestment || 'no',
          otherInvestment: prev.otherInvestment || 'no'
        }));
      }
      
      // Se a resposta for "no" para "Has inherited assets?", limpar os campos de herança
      if (name === 'hasInheritedAssets' && value === 'no') {
        setFormValues(prev => ({
          ...prev,
          valueInherited: 'no',
          relationshipToDeceased: 'no',
          nameOfDeceased: 'no'
        }));
      } else if (name === 'hasInheritedAssets' && value === 'yes') {
        // Quando o usuário seleciona "yes" para hasInheritedAssets, 
        // garantir que todos os campos relacionados comecem com valor "no"
        setFormValues(prev => ({
          ...prev,
          valueInherited: prev.valueInherited || 'no',
          relationshipToDeceased: prev.relationshipToDeceased || 'no',
          nameOfDeceased: prev.nameOfDeceased || 'no'
        }));
      }
      
      // Se a resposta for "no" para "Sold a company or property?", limpar os campos de venda
      if (name === 'soldCompanyProperty' && value === 'no') {
        setFormValues(prev => {
          // Certifica-se de que saleYear e saleValue sejam definidos como "no"
          const updatedValues = {
            ...prev,
            saleYear: 'no',
            saleValue: 'no'
          };
          
          console.log('Definindo saleYear como "no":', updatedValues);
          return updatedValues;
        });
      } else if (name === 'soldCompanyProperty' && value === 'yes') {
        // Quando o usuário seleciona "yes" para soldCompanyProperty, 
        // definir uma data atual com o ano 2020
        const currentDate = new Date();
        currentDate.setFullYear(2020);
        const formattedDate = currentDate.toISOString().split('T')[0]; // Formato YYYY-MM-DD
        
        setFormValues(prev => {
          // Se o saleYear já existir mas for apenas um ano (ex: "2020"),
          // converta para o formato completo YYYY-MM-DD usando o mês e dia atuais
          let normalizedSaleYear = prev.saleYear;
          
          if (normalizedSaleYear && normalizedSaleYear !== 'no' && /^\d{4}$/.test(normalizedSaleYear)) {
            const today = new Date();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            normalizedSaleYear = `${normalizedSaleYear}-${month}-${day}`;
          } else if (normalizedSaleYear === 'no' || !normalizedSaleYear) {
            normalizedSaleYear = formattedDate;
          }
          
          return {
            ...prev,
            saleYear: normalizedSaleYear,
            saleValue: prev.saleValue === 'no' ? '' : prev.saleValue || ''
          };
        });
      }
      
      // Se a resposta for "no" para "Holds political office?", limpar todas as checkbox de detalhes
      if (name === 'holdsPoliticalOffice' && value === 'no') {
        setFormValues(prev => ({
          ...prev,
          isGovernmentOfficial: 'no',
          isJudge: 'no',
          isMilitaryOfficer: 'no',
          isStateCompanyExecutive: 'no',
          isPoliticalPartyLeader: 'no'
        }));
      } else if (name === 'holdsPoliticalOffice' && value === 'yes') {
        // Quando o usuário seleciona "yes" para holdsPoliticalOffice, 
        // garantir que todos os checkboxes comecem com o valor "no"
        setFormValues(prev => ({
          ...prev,
          isGovernmentOfficial: prev.isGovernmentOfficial || 'no',
          isJudge: prev.isJudge || 'no',
          isMilitaryOfficer: prev.isMilitaryOfficer || 'no',
          isStateCompanyExecutive: prev.isStateCompanyExecutive || 'no',
          isPoliticalPartyLeader: prev.isPoliticalPartyLeader || 'no'
        }));
      }
      
      // Se a resposta for "no" para "Has relationship with PEP?", limpar todas as checkbox de detalhes
      if (name === 'hasRelationshipWithPEP' && value === 'no') {
        setFormValues(prev => ({
          ...prev,
          isSpousePartner: 'no',
          isFamily: 'no',
          isBusinessAssociate: 'no'
        }));
      } else if (name === 'hasRelationshipWithPEP' && value === 'yes') {
        // Quando o usuário seleciona "yes" para hasRelationshipWithPEP, 
        // garantir que todos os checkboxes comecem com o valor "no"
        setFormValues(prev => ({
          ...prev,
          isSpousePartner: prev.isSpousePartner || 'no',
          isFamily: prev.isFamily || 'no',
          isBusinessAssociate: prev.isBusinessAssociate || 'no'
        }));
      }
      
      // Se a resposta for "no" para "Has government position?", limpar todos os campos de detalhes
      if (name === 'hasGovernmentPosition' && value === 'no') {
        setFormValues(prev => ({
          ...prev,
          positionTitle: 'no',
          publicFigureName: 'no',
          organizationName: 'no',
          countryJurisdiction: 'no',
          positionStartDate: 'no',
          positionEndDate: 'no'
        }));
      } else if (name === 'hasGovernmentPosition' && value === 'yes') {
        // Quando o usuário seleciona "yes" para hasGovernmentPosition, 
        // garantir que todos os campos relacionados comecem com valor "no"
        setFormValues(prev => ({
          ...prev,
          positionTitle: prev.positionTitle || 'no',
          publicFigureName: prev.publicFigureName || 'no',
          organizationName: prev.organizationName || 'no',
          countryJurisdiction: prev.countryJurisdiction || 'no',
          positionStartDate: prev.positionStartDate || 'no',
          positionEndDate: prev.positionEndDate || 'no'
        }));
      }
      
      // Se a resposta for "no" para "Has government contracts?", limpar os campos de contratos governamentais
      if (name === 'hasGovernmentContracts' && value === 'no') {
        setFormValues(prev => ({
          ...prev,
          contractType: 'no',
          publicEntityInvolved: 'no',
          customerRole: 'no'
        }));
      } else if (name === 'hasGovernmentContracts' && value === 'yes') {
        // Quando o usuário seleciona "yes" para hasGovernmentContracts, 
        // garantir que todos os campos relacionados comecem com valor "no"
        setFormValues(prev => ({
          ...prev,
          contractType: prev.contractType || 'no',
          publicEntityInvolved: prev.publicEntityInvolved || 'no',
          customerRole: prev.customerRole || 'no'
        }));
      }
      
      // Se a resposta for "no" para "Works with agrobusiness?", limpar o campo de detalhes
      if (name === 'worksWithAgrobusiness' && value === 'no') {
        setFormValues(prev => ({
          ...prev
        }));
      } else if (name === 'worksWithAgrobusiness' && value === 'yes') {
        // Quando o usuário seleciona "yes" para worksWithAgrobusiness, 
        // não é mais necessário inicializar campos pois não há mais campos condicionais
        setFormValues(prev => ({
          ...prev
        }));
      }
    } else if (type === 'checkbox') {
      setFormValues(prev => ({
        ...prev,
        [name]: checked ? 'yes' : 'no'
      }));
    } else {
      setFormValues(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Normalizar valores de data
    if (type === 'date' && name === 'saleYear' && value) {
      setFormValues(prev => ({
        ...prev,
        [name]: normalizeDate(value)
      }));
      return;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Mostrar o componente de loading
    setIsLoading(true);
    setIsSubmitting(true);
    
    try {
      // Criar uma cópia dos valores do formulário para manipular antes de enviar
      const formDataToSubmit = { ...formValues };
      
      // Se o campo anySignificantCareer estiver vazio, preencher com "no"
      if (!formDataToSubmit.anySignificantCareer || (typeof formDataToSubmit.anySignificantCareer === 'string' && formDataToSubmit.anySignificantCareer.trim() === '')) {
        formDataToSubmit.anySignificantCareer = 'no';
        
        // Atualizar o estado do formulário para refletir a mudança
        setFormValues(prev => ({
          ...prev,
          anySignificantCareer: 'no'
        }));
      }
      
      // Enviar os dados do formulário para o backend com a versão atualizada
      const response = await submitCompliance(
        ComplianceType.FORM,
        undefined,
        undefined,
        formDataToSubmit
      );
      
      console.log("Resposta recebida da API:", response);
      
      // Criar dados para a modal
      const modalData = {
        // Incluir todos os campos do formulário
        ...formDataToSubmit,
        // Garantir que os campos básicos estejam presentes mesmo se vazios
        name: formDataToSubmit.fullName || '',
        birthDate: formDataToSubmit.birthDate || ''
      };

      // Configurar o state para a navegação
      const navigationState: NavigationState = {
        showModal: true,
        modalData: modalData,
        completed: true,
        resultContent: response.content && response.content[0]?.text ? 
          response.content[0].text : 
          JSON.stringify(response.content && response.content[0]?.complianceForm ? response.content[0].complianceForm : formDataToSubmit, null, 2),
        apiResponse: response,
        fields: response.content && response.content[0]?.complianceForm?.complianceFormSchema
          ? response.content[0].complianceForm.complianceFormSchema
          : formDataToSubmit,
        dataHasBeenSaved: true
      };
      
      console.log("Estado para navegação:", navigationState);
      
      // Navegação bem-sucedida, podemos esconder o loading antes de navegar
      setIsLoading(false);
      
      // Adicionar isNewSubmission ao navigationState
      navigationState.isNewSubmission = true;
      
      // Navegar para a página de resultados pendentes com os dados
      console.log('Navegando para /compliance/pending com dados da resposta');
      navigate('/compliance/pending?full=true', {
        replace: true, // Usar replace para evitar problemas de navegação
        state: navigationState
      });
    } catch (error) {
      console.error('Erro ao enviar o formulário:', error);
      // Mostrar a mensagem específica do erro, se disponível, usando Toast
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Ocorreu um erro ao enviar o formulário. Tente novamente.';
      setToastMessage(errorMessage);
      setShowErrorToast(true);
      setTimeout(() => {
        setShowErrorToast(false);
      }, 3000);
      // Esconder o loading
      setIsLoading(false);
    } finally {
      setIsSubmitting(false);
      // Garantir que o loading seja desativado em qualquer caso
      setIsLoading(false);
    }
  };

  const isSectionValid = () => {
    const currentFields = sections[currentSection].fields;
    
    // Verifica quais campos são considerados relevantes (aplicando lógica condicional)
    const relevantFields = currentFields.filter(field => {
        // Para os campos de educação, só são relevantes se "Has completed college" for "yes"
        if ((field.id === 'institution' || field.id === 'degree' || field.id === 'fieldsOfStudy' || 
             field.id === 'datesOfCompletion' || field.id === 'relevantContinuingEducation')) {
          return formValues.hasCompletedCollege === 'yes';
        }
        
        // Para o campo de setor/atividade, ele só é considerado relevante
        // se a opção "Sim" estiver selecionada
        if (field.id === 'sectorActivity') {
          return formValues.isEntrepreneur === 'yes';
        }
        // Para o campo de tipo de trader, só é considerado se a opção "Sim" estiver selecionada para trader
        if (field.id === 'traderType') {
          return formValues.operateFinancial === 'yes';
        }
        // Para o campo de tipos de investimento, só é considerado se a opção "Sim" estiver selecionada
        if (field.id === 'investmentTypes') {
          return formValues.hasInvestmentIncome === 'yes';
        }
        // Para os campos de herança, só são considerados se "Has inherited assets" for "sim"
        if (field.id === 'inheritanceDetails') {
          return formValues.hasInheritedAssets === 'yes';
        }
        // Para os campos de venda, só são considerados se "Sold a company or property" for "sim"
        if (field.id === 'saleDetails') {
          return formValues.soldCompanyProperty === 'yes';
        }
        // Para os campos de contratos governamentais, só são considerados se "Has government contracts" for "sim"
        if (field.id === 'governmentContractDetails') {
          return formValues.hasGovernmentContracts === 'yes';
        }
        // Para os campos de agronegócio, só são considerados se "Works with agrobusiness" for "sim"
        if (field.id === 'agrobusinessDetails') {
          return formValues.worksWithAgrobusiness === 'yes';
        }
        // Para os campos de cargo governamental, só são considerados se "Has government position" for "sim"
        if (field.id === 'governmentPositionDetails') {
          return formValues.hasGovernmentPosition === 'yes';
        }
        // Para os campos de relacionamento com PEP, só são considerados se "Has relationship with PEP" for "sim"
        if (field.id === 'pepRelationshipDetails') {
          return formValues.hasRelationshipWithPEP === 'yes';
        }
        // Para os campos de cargo político, só são considerados se "Holds political office" for "sim"
        if (field.id === 'politicalOfficeDetails') {
          return formValues.holdsPoliticalOffice === 'yes';
        }
        return true;
    });
    
    // Se não houver campos relevantes, a seção é válida
    if (relevantFields.length === 0) {
      return true;
    }
    
    const allValid = relevantFields.every(field => {
        // Para debugging
        
        // Tratamento especial para o campo "Have you completed a college?"
        if (field.id === 'hasCompletedCollege') {
          const isValid = formValues.hasCompletedCollege === 'yes' || formValues.hasCompletedCollege === 'no';
          return isValid;
        }
        
        // Tratamento especial para os campos de educação
        if ((field.id === 'institution' || field.id === 'degree') && formValues.hasCompletedCollege === 'yes') {
          const isValid = formValues[field.id] && formValues[field.id].toString().trim() !== '';
          return isValid;
        }
        
        // Tratamento especial para campos de educação opcionais
        if ((field.id === 'fieldsOfStudy' || field.id === 'datesOfCompletion' || field.id === 'relevantContinuingEducation') && formValues.hasCompletedCollege === 'yes') {
          const isValid = formValues[field.id] && formValues[field.id].toString().trim() !== '';
          return isValid;
        }
        
        // Tratamento especial para o campo de empreendedor/consultor que agora usa radio buttons
        if (field.id === 'entrepreneur') {
          const isValid = formValues.isEntrepreneur === 'yes' || formValues.isEntrepreneur === 'no';
          return isValid;
        }
        
        // Tratamento especial para o campo de operar no mercado financeiro
        if (field.id === 'trader') {
          const isValid = formValues.operateFinancial === 'yes' || formValues.operateFinancial === 'no';
          return isValid;
        }
        
        // Tratamento especial para o campo de renda de investimentos
        if (field.id === 'hasInvestmentIncome') {
          const isValid = formValues.hasInvestmentIncome === 'yes' || formValues.hasInvestmentIncome === 'no';
          return isValid;
        }
        
        // Tratamento especial para o campo de ativos herdados
        if (field.id === 'hasInheritedAssets') {
          const isValid = formValues.hasInheritedAssets === 'yes' || formValues.hasInheritedAssets === 'no';
          return isValid;
        }
        
        // Tratamento especial para o campo de venda de empresa/propriedade
        if (field.id === 'soldCompanyProperty') {
          const isValid = formValues.soldCompanyProperty === 'yes' || formValues.soldCompanyProperty === 'no';
          return isValid;
        }
        
        // Tratamento especial para o campo de contratos governamentais
        if (field.id === 'hasGovernmentContracts') {
          const isValid = formValues.hasGovernmentContracts === 'yes' || formValues.hasGovernmentContracts === 'no';
          return isValid;
        }
        
        // Tratamento especial para o campo de agronegócio
        if (field.id === 'worksWithAgrobusiness') {
          const isValid = formValues.worksWithAgrobusiness === 'yes' || formValues.worksWithAgrobusiness === 'no';
          return isValid;
        }
        
        // Tratamento especial para o campo de propriedade de negócios
        if (field.id === 'ownsBusinessOwnership') {
          const isValid = formValues.ownsBusinessOwnership === 'yes' || formValues.ownsBusinessOwnership === 'no';
          return isValid;
        }
        
        // Tratamento especial para o campo de cargo político
        if (field.id === 'holdsPoliticalOffice') {
          const isValid = formValues.holdsPoliticalOffice === 'yes' || formValues.holdsPoliticalOffice === 'no';
          return isValid;
        }
        
        // Tratamento especial para o campo de relacionamento com PEP
        if (field.id === 'hasRelationshipWithPEP') {
          const isValid = formValues.hasRelationshipWithPEP === 'yes' || formValues.hasRelationshipWithPEP === 'no';
          return isValid;
        }
        
        // Tratamento especial para o campo de cargo governamental
        if (field.id === 'hasGovernmentPosition') {
          const isValid = formValues.hasGovernmentPosition === 'yes' || formValues.hasGovernmentPosition === 'no';
          return isValid;
        }
        
        // Tratamento especial para o campo de tipo de trader (só relevante se for trader)
        if (field.id === 'traderType' && formValues.operateFinancial === 'yes') {
          const isValid = formValues.traderType === 'proprietary' || formValues.traderType === 'brokerage';
          return isValid;
        }
        
        // Tratamento especial para o campo condicional de setor/atividade
        if (field.id === 'sectorActivity' && formValues.isEntrepreneur === 'yes') {
          const isValid = formValues.sectorActivity && (typeof formValues.sectorActivity === 'string' ? formValues.sectorActivity.trim() !== '' : formValues.sectorActivity !== '');
          return isValid;
        }
        
        // Tratamento especial para o campo condicional de tipos de investimento
        if (field.id === 'investmentTypes' && formValues.hasInvestmentIncome === 'yes') {
          const isValid = formValues.realEstateInvestment === 'yes' || 
                formValues.privateEquityInvestment === 'yes' || 
                formValues.otherInvestment === 'yes';
          return isValid;
        }
        
        // Tratamento especial para o campo de tipos de investimento quando hasInvestmentIncome é "no"
        if (field.id === 'investmentTypes' && formValues.hasInvestmentIncome === 'no') {
          return true; // Sempre válido quando hasInvestmentIncome é "no", pois otherInvestment será automaticamente "yes"
        }
        
        // Para os campos de herança (devem estar preenchidos se "Has inherited assets" for "sim")
        if (field.id === 'inheritanceDetails' && formValues.hasInheritedAssets === 'yes') {
          const isValid = formValues.valueInherited && 
                formValues.relationshipToDeceased && (typeof formValues.relationshipToDeceased === 'string' ? formValues.relationshipToDeceased.trim() !== '' : formValues.relationshipToDeceased !== '') &&
                formValues.nameOfDeceased && (typeof formValues.nameOfDeceased === 'string' ? formValues.nameOfDeceased.trim() !== '' : formValues.nameOfDeceased !== '');
          return isValid;
        }
        
        // Para os campos de venda (devem estar preenchidos se "Sold a company or property" for "sim")
        if (field.id === 'saleDetails' && formValues.soldCompanyProperty === 'yes') {
          const isValid = formValues.saleYear && formValues.saleValue;
          return isValid;
        }
        
        // Para os campos de contratos governamentais (devem estar preenchidos se "Has government contracts" for "sim")
        if (field.id === 'governmentContractDetails' && formValues.hasGovernmentContracts === 'yes') {
          const isValid = formValues.contractType && (typeof formValues.contractType === 'string' ? formValues.contractType.trim() !== '' : formValues.contractType !== '') &&
                formValues.publicEntityInvolved && (typeof formValues.publicEntityInvolved === 'string' ? formValues.publicEntityInvolved.trim() !== '' : formValues.publicEntityInvolved !== '') &&
                formValues.customerRole && (typeof formValues.customerRole === 'string' ? formValues.customerRole.trim() !== '' : formValues.customerRole !== '');
          return isValid;
        }
        
        // Tratamento especial para os campos de agronegócio (devem estar preenchidos se "Works with agrobusiness" for "sim")
        if (field.id === 'agrobusinessDetails' && formValues.worksWithAgrobusiness === 'yes') {
          // Não há mais campos para validar aqui, então sempre retornamos verdadeiro
          return true;
        }
        
        // Tratamento especial para os campos de cargo governamental (todos os campos devem estar preenchidos)
        if (field.id === 'governmentPositionDetails' && formValues.hasGovernmentPosition === 'yes') {
          const isValid = formValues.positionTitle && (typeof formValues.positionTitle === 'string' ? formValues.positionTitle.trim() !== '' : formValues.positionTitle !== '') &&
                 formValues.publicFigureName && (typeof formValues.publicFigureName === 'string' ? formValues.publicFigureName.trim() !== '' : formValues.publicFigureName !== '') &&
                 formValues.organizationName && (typeof formValues.organizationName === 'string' ? formValues.organizationName.trim() !== '' : formValues.organizationName !== '') &&
                 formValues.countryJurisdiction && (typeof formValues.countryJurisdiction === 'string' ? formValues.countryJurisdiction.trim() !== '' : formValues.countryJurisdiction !== '') &&
                 formValues.positionStartDate &&
                 formValues.positionEndDate;
          return isValid;
        }
        
        // Tratamento especial para os campos de relacionamento com PEP (pelo menos uma opção deve estar marcada)
        if (field.id === 'pepRelationshipDetails' && formValues.hasRelationshipWithPEP === 'yes') {
          const isValid = formValues.isSpousePartner === 'yes' || 
                formValues.isFamily === 'yes' || 
                formValues.isBusinessAssociate === 'yes';
          return isValid;
        }

        // Tratamento especial para os campos de cargo político (pelo menos uma opção deve estar marcada)
        if (field.id === 'politicalOfficeDetails' && formValues.holdsPoliticalOffice === 'yes') {
          const isValid = formValues.isGovernmentOfficial === 'yes' || 
                formValues.isJudge === 'yes' || 
                formValues.isMilitaryOfficer === 'yes' ||
                formValues.isStateCompanyExecutive === 'yes' ||
                formValues.isPoliticalPartyLeader === 'yes';
          return isValid;
        }
        
        // Validação padrão para outros campos
        const fieldValue = formValues[field.id];
        // Se o campo existir e não for undefined ou null
        if (fieldValue !== undefined && fieldValue !== null) {
          const stringValue = fieldValue.toString();
          const isValid = typeof stringValue === 'string' ? stringValue.trim() !== '' : stringValue !== '';
          return isValid;
        } else {
          return false;
        }
    });
    
    return allValid;
  };

  const handleNext = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(prevSection => prevSection + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrev = () => {
    if (currentSection > 0) {
      setCurrentSection(prevSection => prevSection - 1);
      window.scrollTo(0, 0);
    }
  };

  // Calcular o progresso geral
  const calculateTotalProgress = () => {
    const completedSections = sectionProgress.filter(progress => progress === 100).length;
    return Math.round((completedSections / sections.length) * 100);
  };

  const isFormValid = () => {
    // Verificar todos os campos relevantes em todas as seções
    for (let index = 0; index < sections.length; index++) {
      const section = sections[index];
      
      // Filtrar campos relevantes (aplicando lógica condicional)
      const relevantFields = section.fields.filter(field => {
        // Para os campos de educação, só são relevantes se "Has completed college" for "yes"
        if ((field.id === 'institution' || field.id === 'degree' || field.id === 'fieldsOfStudy' || 
             field.id === 'datesOfCompletion' || field.id === 'relevantContinuingEducation')) {
          return formValues.hasCompletedCollege === 'yes';
        }
        
        // Para o campo de setor/atividade, só é relevante se for empreendedor/consultor
        if (field.id === 'sectorActivity') {
          return formValues.isEntrepreneur === 'yes';
        }
        
        // Para o campo de tipo de trader, só é relevante se for trader
        if (field.id === 'traderType') {
          return formValues.operateFinancial === 'yes';
        }
        
        // Para o campo de tipos de investimento, só é relevante se tiver renda de investimentos
        if (field.id === 'investmentTypes') {
          return formValues.hasInvestmentIncome === 'yes';
        }
        
        // Para os campos de herança, só são relevantes se tiver ativos herdados
        if (field.id === 'inheritanceDetails') {
          return formValues.hasInheritedAssets === 'yes';
        }
        
        // Para os campos de venda, só são relevantes se vendeu empresa/propriedade
        if (field.id === 'saleDetails') {
          return formValues.soldCompanyProperty === 'yes';
        }
        
        // Para os campos de contratos governamentais, só são relevantes se tiver contratos
        if (field.id === 'governmentContractDetails') {
          return formValues.hasGovernmentContracts === 'yes';
        }
        
        // Para os campos de agronegócio, só são relevantes se trabalhar com agronegócio
        if (field.id === 'agrobusinessDetails') {
          return formValues.worksWithAgrobusiness === 'yes';
        }
        
        // Para os campos de cargo governamental, só são relevantes se tiver cargo governamental
        if (field.id === 'governmentPositionDetails') {
          return formValues.hasGovernmentPosition === 'yes';
        }
        
        // Para os campos de relacionamento com PEP, só são relevantes se tiver relacionamento
        if (field.id === 'pepRelationshipDetails') {
          return formValues.hasRelationshipWithPEP === 'yes';
        }
        
        // Para os campos de cargo político, só são relevantes se tiver cargo político
        if (field.id === 'politicalOfficeDetails') {
          return formValues.holdsPoliticalOffice === 'yes';
        }
        
        // Para outros campos, sempre considerar como relevante
        return true;
      });
      
      // Se não houver campos relevantes, a seção é válida
      if (relevantFields.length === 0) {
        continue;
      }
      
      // Verificar se todos os campos relevantes estão preenchidos
      const allFieldsValid = relevantFields.every(field => {
        // Verificações específicas para diferentes tipos de campos
        
        // Para o campo hasCompletedCollege
        if (field.id === 'hasCompletedCollege') {
          return formValues.hasCompletedCollege === 'yes' || formValues.hasCompletedCollege === 'no';
        }
        
        // Para os campos de educação
        if ((field.id === 'institution' || field.id === 'degree') && formValues.hasCompletedCollege === 'yes') {
          return formValues[field.id] && formValues[field.id].trim() !== '';
        }
        
        // Para campos de educação opcionais
        if ((field.id === 'fieldsOfStudy' || field.id === 'datesOfCompletion' || field.id === 'relevantContinuingEducation') && formValues.hasCompletedCollege === 'yes') {
          return formValues[field.id] && formValues[field.id].trim() !== '';
        }
        
        // Para o campo de empreendedor/consultor
        if (field.id === 'entrepreneur') {
          return formValues.isEntrepreneur === 'yes' || formValues.isEntrepreneur === 'no';
        }
        
        // Para o campo de trader
        if (field.id === 'trader') {
          return formValues.operateFinancial === 'yes' || formValues.operateFinancial === 'no';
        }
        
        // Para o campo de tipo de trader
        if (field.id === 'traderType' && formValues.operateFinancial === 'yes') {
          return formValues.traderType === 'proprietary' || formValues.traderType === 'brokerage';
        }
        
        // Para o campo de setor/atividade
        if (field.id === 'sectorActivity' && formValues.isEntrepreneur === 'yes') {
          return formValues.sectorActivity && formValues.sectorActivity.trim() !== '';
        }
        
        // Para o campo de renda de investimentos
        if (field.id === 'hasInvestmentIncome') {
          return formValues.hasInvestmentIncome === 'yes' || formValues.hasInvestmentIncome === 'no';
        }
        
        // Para o campo de tipos de investimento
        if (field.id === 'investmentTypes' && formValues.hasInvestmentIncome === 'yes') {
          return formValues.realEstateInvestment === 'yes' || 
                formValues.privateEquityInvestment === 'yes' || 
                formValues.otherInvestment === 'yes';
        }
        
        // Para o campo de tipos de investimento - caso especial quando hasInvestmentIncome é "no"
        if (field.id === 'investmentTypes' && formValues.hasInvestmentIncome === 'no') {
          return true; // Sempre válido quando hasInvestmentIncome é "no", pois otherInvestment será automaticamente "yes"
        }
        
        // Para os campos de ativos herdados
        if (field.id === 'hasInheritedAssets') {
          return formValues.hasInheritedAssets === 'yes' || formValues.hasInheritedAssets === 'no';
        }
        
        // Para os detalhes de herança
        if (field.id === 'inheritanceDetails' && formValues.hasInheritedAssets === 'yes') {
          return formValues.valueInherited && 
                formValues.relationshipToDeceased && formValues.relationshipToDeceased.trim() !== '' &&
                formValues.nameOfDeceased && formValues.nameOfDeceased.trim() !== '';
        }
        
        // Para o campo de venda de empresa/propriedade
        if (field.id === 'soldCompanyProperty') {
          return formValues.soldCompanyProperty === 'yes' || formValues.soldCompanyProperty === 'no';
        }
        
        // Para os detalhes de venda
        if (field.id === 'saleDetails' && formValues.soldCompanyProperty === 'yes') {
          return formValues.saleYear && formValues.saleValue;
        }
        
        // Para o campo de contratos governamentais
        if (field.id === 'hasGovernmentContracts') {
          return formValues.hasGovernmentContracts === 'yes' || formValues.hasGovernmentContracts === 'no';
        }
        
        // Para os detalhes de contratos governamentais
        if (field.id === 'governmentContractDetails' && formValues.hasGovernmentContracts === 'yes') {
          return formValues.contractType && formValues.contractType.trim() !== '' &&
                formValues.publicEntityInvolved && formValues.publicEntityInvolved.trim() !== '' &&
                formValues.customerRole && formValues.customerRole.trim() !== '';
        }
        
        // Para o campo de agronegócio
        if (field.id === 'worksWithAgrobusiness') {
          return formValues.worksWithAgrobusiness === 'yes' || formValues.worksWithAgrobusiness === 'no';
        }
        
        // Para o campo de cargo político
        if (field.id === 'holdsPoliticalOffice') {
          return formValues.holdsPoliticalOffice === 'yes' || formValues.holdsPoliticalOffice === 'no';
        }
        
        // Para os detalhes de cargo político
        if (field.id === 'politicalOfficeDetails' && formValues.holdsPoliticalOffice === 'yes') {
          return formValues.isGovernmentOfficial === 'yes' || 
                formValues.isJudge === 'yes' || 
                formValues.isMilitaryOfficer === 'yes' ||
                formValues.isStateCompanyExecutive === 'yes' ||
                formValues.isPoliticalPartyLeader === 'yes';
        }
        
        // Para o campo de relacionamento com PEP
        if (field.id === 'hasRelationshipWithPEP') {
          return formValues.hasRelationshipWithPEP === 'yes' || formValues.hasRelationshipWithPEP === 'no';
        }
        
        // Para os detalhes de relacionamento com PEP
        if (field.id === 'pepRelationshipDetails' && formValues.hasRelationshipWithPEP === 'yes') {
          return formValues.isSpousePartner === 'yes' || 
                formValues.isFamily === 'yes' || 
                formValues.isBusinessAssociate === 'yes';
        }
        
        // Para o campo de cargo governamental
        if (field.id === 'hasGovernmentPosition') {
          return formValues.hasGovernmentPosition === 'yes' || formValues.hasGovernmentPosition === 'no';
        }
        
        // Para os detalhes de cargo governamental
        if (field.id === 'governmentPositionDetails' && formValues.hasGovernmentPosition === 'yes') {
          return formValues.positionTitle && formValues.positionTitle.trim() !== '' &&
                formValues.publicFigureName && formValues.publicFigureName.trim() !== '' &&
                formValues.organizationName && formValues.organizationName.trim() !== '' &&
                formValues.countryJurisdiction && formValues.countryJurisdiction.trim() !== '' &&
                formValues.positionStartDate &&
                formValues.positionEndDate;
        }
        
        // Para campos padrão (texto, número, data, etc.)
        const fieldValue = formValues[field.id];
        if (fieldValue !== undefined && fieldValue !== null) {
          const stringValue = fieldValue.toString();
          return typeof stringValue === 'string' ? stringValue.trim() !== '' : stringValue !== '';
        } else {
          return false;
        }
      });
      
      if (!allFieldsValid) {
        return false;
      }
    }
    
    return true;
  };

  return (
    <Container>
      <Header>
        <Tooltip text="Back to Compliance">
          <BackButton onClick={handleBack}>
            {renderIcon(FiArrowLeft, { className: "icon", size: 30 })}
          </BackButton>
        </Tooltip>
      </Header>

      <TitleContainer>
        <Title>Compliance</Title>
        <Subtitle>This is your compliance area, choose the best method to extract the necessary information with the help of AI.</Subtitle>

        <FormContainer>
          <FormTitle>KYC Form</FormTitle>
          <FormDescription>Answer the questions below to gather the information in a document</FormDescription>
          <RequiredFieldsNote>The fields marked with an asterisk (*) are required</RequiredFieldsNote>
          
          {/* Timeline mostrando as 7 seções */}
          <TimelineContainer>
            <TimelineBackground />
            <TimelineProgress style={{ width: `${(currentSection / (sections.length - 1)) * 100}%` }} />
            
            {sections.map((section, index) => (
              <StepItem 
                key={section.id} 
                active={currentSection === index}
                completed={sectionProgress[index] === 100}
                onClick={() => setCurrentSection(index)}
                style={{ cursor: 'pointer' }}
              >
                <StepCircle 
                  active={currentSection === index} 
                  completed={sectionProgress[index] === 100}
                >
                  {sectionProgress[index] === 100 ? renderIcon(FiCheck, { size: 16 }) : section.id}
                </StepCircle>
                <StepLabel active={currentSection === index}>
                  {section.title}
                </StepLabel>
              </StepItem>
            ))}
          </TimelineContainer>
          
          {/* Status de progresso */}
          <SectionStatus>
            <StatusText>
              
              Current section: {sectionProgress[currentSection]}% |
              Total progress: {calculateTotalProgress()}%  
            </StatusText>
          </SectionStatus>
          <ProgressBar>
            <ProgressFill progress={calculateTotalProgress()} />
          </ProgressBar>

          <Form onSubmit={handleSubmit}>
            {/* Seção atual */}
            <FormSection>
              <SectionTitle>{sections[currentSection].title}</SectionTitle>
              <SectionDescription>{sections[currentSection].description}</SectionDescription>
              
              {sections[currentSection].fields.map(field => {
                // Renderização especial para o campo de empreendedor/consultor
                if (field.id === 'entrepreneur') {
                  return (
                    <FormGroup key={field.id}>
                      <Label htmlFor={field.id}>
                        {field.label} {field.required && <Obrigatorio>*</Obrigatorio>}
                      </Label>
                      <RadioGroup>
                        <RadioOption>
                          <Radio 
                            id="isEntrepreneurYes"
                            type="radio"
                            name="isEntrepreneur"
                            value="yes"
                            checked={formValues.isEntrepreneur === 'yes'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="isEntrepreneurYes">Yes</RadioLabel>
                        </RadioOption>
                        <RadioOption>
                          <Radio 
                            id="isEntrepreneurNo"
                            type="radio"
                            name="isEntrepreneur"
                            value="no"
                            checked={formValues.isEntrepreneur === 'no'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="isEntrepreneurNo">No</RadioLabel>
                        </RadioOption>
                      </RadioGroup>
                    </FormGroup>
                  );
                }
                
                // Renderização especial para o campo "Operate in the financial market?"
                if (field.id === 'trader') {
                  return (
                    <FormGroup key={field.id}>
                      <Label htmlFor={field.id}>
                        {field.label} {field.required && <Obrigatorio>*</Obrigatorio>}
                      </Label>
                      <RadioGroup>
                        <RadioOption>
                          <Radio 
                            id="operateFinancialYes"
                            type="radio"
                            name="operateFinancial"
                            value="yes"
                            checked={formValues.operateFinancial === 'yes'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="operateFinancialYes">Yes</RadioLabel>
                        </RadioOption>
                        <RadioOption>
                          <Radio 
                            id="operateFinancialNo"
                            type="radio"
                            name="operateFinancial"
                            value="no"
                            checked={formValues.operateFinancial === 'no'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="operateFinancialNo">No</RadioLabel>
                        </RadioOption>
                      </RadioGroup>
                    </FormGroup>
                  );
                }

                // Renderização especial para o campo "trader próprio ou trabalha em uma corretora?"
                if (field.id === 'traderType') {
                  return (
                    <ConditionalField key={field.id} visible={formValues.operateFinancial === 'yes'}>
                      <FormGroup>
                        <Label htmlFor={field.id}>
                          {field.label} {field.required && <Obrigatorio>*</Obrigatorio>}
                        </Label>
                        <RadioGroup>
                          <RadioOption>
                            <Radio 
                              id="traderTypePropriety"
                              type="radio"
                              name="traderType"
                              value="proprietary"
                              checked={formValues.traderType === 'proprietary'}
                              onChange={handleChange}
                              required={field.required && formValues.operateFinancial === 'yes'}
                              disabled={formValues.operateFinancial !== 'yes'}
                            />
                            <RadioLabel htmlFor="traderTypePropriety">Proprietary Trader</RadioLabel>
                          </RadioOption>
                          <RadioOption>
                            <Radio 
                              id="traderTypeBrokerage"
                              type="radio"
                              name="traderType"
                              value="brokerage"
                              checked={formValues.traderType === 'brokerage'}
                              onChange={handleChange}
                              required={field.required && formValues.operateFinancial === 'yes'}
                              disabled={formValues.operateFinancial !== 'yes'}
                            />
                            <RadioLabel htmlFor="traderTypeBrokerage">Work at a Brokerage</RadioLabel>
                          </RadioOption>
                        </RadioGroup>
                      </FormGroup>
                    </ConditionalField>
                  );
                }
                
                // Renderização especial para o campo de setor/atividade (campo condicional)
                if (field.id === 'sectorActivity') {
                  return (
                    <ConditionalField key={field.id} visible={formValues.isEntrepreneur === 'yes'}>
                      <FormGroup>
                        <Label htmlFor={field.id}>
                          {field.label} {field.required && <Obrigatorio>*</Obrigatorio>}
                        </Label>
                        <Input 
                          id="sectorActivity"
                          type="text"
                          name="sectorActivity"
                          value={formValues.sectorActivity || ''}
                          onChange={handleChange}
                          disabled={formValues.isEntrepreneur !== 'yes'}
                          required={field.required && formValues.isEntrepreneur === 'yes'}
                        />
                      </FormGroup>
                    </ConditionalField>
                  );
                }

                // Renderização especial para o campo "Has investment income?"
                if (field.id === 'hasInvestmentIncome') {
                  return (
                    <FormGroup key={field.id}>
                      <Label htmlFor={field.id}>
                        {field.label} {field.required && <Obrigatorio>*</Obrigatorio>}
                      </Label>
                      <RadioGroup>
                        <RadioOption>
                          <Radio 
                            id="hasInvestmentIncomeYes"
                            type="radio"
                            name="hasInvestmentIncome"
                            value="yes"
                            checked={formValues.hasInvestmentIncome === 'yes'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="hasInvestmentIncomeYes">Yes</RadioLabel>
                        </RadioOption>
                        <RadioOption>
                          <Radio 
                            id="hasInvestmentIncomeNo"
                            type="radio"
                            name="hasInvestmentIncome"
                            value="no"
                            checked={formValues.hasInvestmentIncome === 'no'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="hasInvestmentIncomeNo">No</RadioLabel>
                        </RadioOption>
                      </RadioGroup>
                    </FormGroup>
                  );
                }

                // Renderização para os checkboxes de tipos de investimento
                if (field.id === 'investmentTypes') {
                  return (
                    <ConditionalField key={field.id} visible={formValues.hasInvestmentIncome === 'yes'}>
                      <FormGroup>
                        <Label htmlFor={field.id}>
                          {field.label} {field.required && <Obrigatorio>*</Obrigatorio>}
                        </Label>
                        <CheckboxGroupHorizontal>
                          <CheckboxWithLabel>
                            <Checkbox 
                              id="realEstateInvestment"
                              type="checkbox"
                              name="realEstateInvestment"
                              checked={formValues.realEstateInvestment === 'yes'}
                              onChange={handleChange}
                              disabled={formValues.hasInvestmentIncome !== 'yes'}
                            />
                            <CheckboxLabel htmlFor="realEstateInvestment">Real Estate</CheckboxLabel>
                          </CheckboxWithLabel>
                          <CheckboxWithLabel>
                            <Checkbox 
                              id="privateEquityInvestment"
                              type="checkbox"
                              name="privateEquityInvestment"
                              checked={formValues.privateEquityInvestment === 'yes'}
                              onChange={handleChange}
                              disabled={formValues.hasInvestmentIncome !== 'yes'}
                            />
                            <CheckboxLabel htmlFor="privateEquityInvestment">Private Equity</CheckboxLabel>
                          </CheckboxWithLabel>
                          {/* Campo otherInvestment mantido no estado mas oculto na interface */}
                          <input 
                            type="hidden"
                            name="otherInvestment"
                            value={formValues.hasInvestmentIncome === 'no' ? 'yes' : formValues.otherInvestment}
                          />
                        </CheckboxGroupHorizontal>
                      </FormGroup>
                    </ConditionalField>
                  );
                }
                
                // Renderização para o campo "Sold a company or property?"
                if (field.id === 'soldCompanyProperty') {
                  return (
                    <FormGroup key={field.id}>
                      <Label htmlFor={field.id}>
                        {field.label} {field.required && <Obrigatorio>*</Obrigatorio>}
                      </Label>
                      <RadioGroup>
                        <RadioOption>
                          <Radio 
                            id="soldCompanyPropertyYes"
                            type="radio"
                            name="soldCompanyProperty"
                            value="yes"
                            checked={formValues.soldCompanyProperty === 'yes'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="soldCompanyPropertyYes">Yes</RadioLabel>
                        </RadioOption>
                        <RadioOption>
                          <Radio 
                            id="soldCompanyPropertyNo"
                            type="radio"
                            name="soldCompanyProperty"
                            value="no"
                            checked={formValues.soldCompanyProperty === 'no'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="soldCompanyPropertyNo">No</RadioLabel>
                        </RadioOption>
                      </RadioGroup>
                    </FormGroup>
                  );
                }

                // Renderização para os detalhes da venda (só aparece se "Sold a company or property?" for "yes")
                if (field.id === 'saleDetails') {
                  return (
                    <ConditionalField key={field.id} visible={formValues.soldCompanyProperty === 'yes'}>
                      <div style={{ marginBottom: '24px' }}>
                        <FormGroup>
                          <Label htmlFor="saleYear">
                            Year <Obrigatorio>*</Obrigatorio>
                          </Label>
                          {formValues.soldCompanyProperty === 'no' ? (
                            <Input 
                              id="saleYear"
                              type="hidden"
                              name="saleYear"
                              value="no"
                            />
                          ) : (
                            <Input 
                              id="saleYear"
                              type="date"
                              name="saleYear"
                              value={formValues.saleYear || ''}
                              onChange={handleChange}
                              disabled={formValues.soldCompanyProperty !== 'yes'}
                              required={formValues.soldCompanyProperty === 'yes'}
                            />
                          )}
                        </FormGroup>
                        
                        <FormGroup style={{ marginTop: '16px' }}>
                          <Label htmlFor="saleValue">
                            Value <Obrigatorio>*</Obrigatorio>
                          </Label>
                          <Input 
                            id="saleValue"
                            type="number"
                            name="saleValue"
                            value={formValues.saleValue || ''}
                            onChange={handleChange}
                            disabled={formValues.soldCompanyProperty !== 'yes'}
                            required={formValues.soldCompanyProperty === 'yes'}
                          />
                        </FormGroup>
                      </div>
                    </ConditionalField>
                  );
                }
                
                // Renderização para o campo "Works with agrobusiness?"
                if (field.id === 'worksWithAgrobusiness') {
                  return (
                    <FormGroup key={field.id}>
                      <Label htmlFor={field.id}>
                        {field.label} {field.required && <Obrigatorio>*</Obrigatorio>}
                      </Label>
                      <RadioGroup>
                        <RadioOption>
                          <Radio 
                            id="worksWithAgrobusinessYes"
                            type="radio"
                            name="worksWithAgrobusiness"
                            value="yes"
                            checked={formValues.worksWithAgrobusiness === 'yes'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="worksWithAgrobusinessYes">Yes</RadioLabel>
                        </RadioOption>
                        <RadioOption>
                          <Radio 
                            id="worksWithAgrobusinessNo"
                            type="radio"
                            name="worksWithAgrobusiness"
                            value="no"
                            checked={formValues.worksWithAgrobusiness === 'no'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="worksWithAgrobusinessNo">No</RadioLabel>
                        </RadioOption>
                      </RadioGroup>
                    </FormGroup>
                  );
                }
                
                // Renderização para o campo "Owns business ownership?"
                if (field.id === 'ownsBusinessOwnership') {
                  return (
                    <FormGroup key={field.id}>
                      <Label htmlFor={field.id}>
                        {field.label} {field.required && <Obrigatorio>*</Obrigatorio>}
                      </Label>
                      <RadioGroup>
                        <RadioOption>
                          <Radio 
                            id="ownsBusinessOwnershipYes"
                            type="radio"
                            name="ownsBusinessOwnership"
                            value="yes"
                            checked={formValues.ownsBusinessOwnership === 'yes'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="ownsBusinessOwnershipYes">Yes</RadioLabel>
                        </RadioOption>
                        <RadioOption>
                          <Radio 
                            id="ownsBusinessOwnershipNo"
                            type="radio"
                            name="ownsBusinessOwnership"
                            value="no"
                            checked={formValues.ownsBusinessOwnership === 'no'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="ownsBusinessOwnershipNo">No</RadioLabel>
                        </RadioOption>
                      </RadioGroup>
                    </FormGroup>
                  );
                }
                
                // Renderização para os detalhes de agronegócio
                if (field.id === 'agrobusinessDetails') {
                  return (
                    <ConditionalField key={field.id} visible={formValues.worksWithAgrobusiness === 'yes'}>
                      <div style={{ marginBottom: '24px' }}>
                        {/* O campo cropsProductsInvolved foi removido */}
                      </div>
                    </ConditionalField>
                  );
                }
                
                // Renderização para o campo "Holds political office?"
                if (field.id === 'holdsPoliticalOffice') {
                  return (
                    <FormGroup key={field.id}>
                      <Label htmlFor={field.id}>
                        {field.label} {field.required && <Obrigatorio>*</Obrigatorio>}
                      </Label>
                      <RadioGroup>
                        <RadioOption>
                          <Radio 
                            id="holdsPoliticalOfficeYes"
                            type="radio"
                            name="holdsPoliticalOffice"
                            value="yes"
                            checked={formValues.holdsPoliticalOffice === 'yes'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="holdsPoliticalOfficeYes">Yes</RadioLabel>
                        </RadioOption>
                        <RadioOption>
                          <Radio 
                            id="holdsPoliticalOfficeNo"
                            type="radio"
                            name="holdsPoliticalOffice"
                            value="no"
                            checked={formValues.holdsPoliticalOffice === 'no'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="holdsPoliticalOfficeNo">No</RadioLabel>
                        </RadioOption>
                      </RadioGroup>
                    </FormGroup>
                  );
                }
                
                // Renderização para os detalhes de cargo político
                if (field.id === 'politicalOfficeDetails') {
                  return (
                    <ConditionalField key={field.id} visible={formValues.holdsPoliticalOffice === 'yes'}>
                      <div style={{ marginBottom: '24px' }}>
                        <FormGroup>
                          <div style={{ textAlign: 'center', marginBottom: '12px', fontWeight: 'bold' }}>
                            <Obrigatorio>*</Obrigatorio> Select at least one option:
                          </div>
                          <div style={{ marginTop: '8px', marginLeft: '20%', width: '60%' }}>
                            <CheckboxWithLabel style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                              <Checkbox 
                                id="isGovernmentOfficial"
                                type="checkbox"
                                name="isGovernmentOfficial"
                                checked={formValues.isGovernmentOfficial === 'yes'}
                                onChange={handleChange}
                                disabled={formValues.holdsPoliticalOffice !== 'yes'}
                              />
                              <CheckboxLabel htmlFor="isGovernmentOfficial" style={{ marginLeft: '10px' }}>
                                Government official (municipal, state, or federal)
                              </CheckboxLabel>
                            </CheckboxWithLabel>
                            
                            <CheckboxWithLabel style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                              <Checkbox 
                                id="isJudge"
                                type="checkbox"
                                name="isJudge"
                                checked={formValues.isJudge === 'yes'}
                                onChange={handleChange}
                                disabled={formValues.holdsPoliticalOffice !== 'yes'}
                              />
                              <CheckboxLabel htmlFor="isJudge" style={{ marginLeft: '10px' }}>
                                Judge or magistrate
                              </CheckboxLabel>
                            </CheckboxWithLabel>
                            
                            <CheckboxWithLabel style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                              <Checkbox 
                                id="isMilitaryOfficer"
                                type="checkbox"
                                name="isMilitaryOfficer"
                                checked={formValues.isMilitaryOfficer === 'yes'}
                                onChange={handleChange}
                                disabled={formValues.holdsPoliticalOffice !== 'yes'}
                              />
                              <CheckboxLabel htmlFor="isMilitaryOfficer" style={{ marginLeft: '10px' }}>
                                Military officer
                              </CheckboxLabel>
                            </CheckboxWithLabel>
                            
                            <CheckboxWithLabel style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                              <Checkbox 
                                id="isStateCompanyExecutive"
                                type="checkbox"
                                name="isStateCompanyExecutive"
                                checked={formValues.isStateCompanyExecutive === 'yes'}
                                onChange={handleChange}
                                disabled={formValues.holdsPoliticalOffice !== 'yes'}
                              />
                              <CheckboxLabel htmlFor="isStateCompanyExecutive" style={{ marginLeft: '10px' }}>
                                Executive at a state-owned company or public agency
                              </CheckboxLabel>
                            </CheckboxWithLabel>
                            
                            <CheckboxWithLabel style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                              <Checkbox 
                                id="isPoliticalPartyLeader"
                                type="checkbox"
                                name="isPoliticalPartyLeader"
                                checked={formValues.isPoliticalPartyLeader === 'yes'}
                                onChange={handleChange}
                                disabled={formValues.holdsPoliticalOffice !== 'yes'}
                              />
                              <CheckboxLabel htmlFor="isPoliticalPartyLeader" style={{ marginLeft: '10px' }}>
                                Senior political party leader
                              </CheckboxLabel>
                            </CheckboxWithLabel>
                          </div>
                          {formValues.holdsPoliticalOffice === 'yes' && 
                           !(formValues.isGovernmentOfficial === 'yes' || 
                             formValues.isJudge === 'yes' || 
                             formValues.isMilitaryOfficer === 'yes' ||
                             formValues.isStateCompanyExecutive === 'yes' ||
                             formValues.isPoliticalPartyLeader === 'yes') && (
                            <div style={{ color: theme.error, marginTop: '8px', fontSize: '14px', textAlign: 'center' }}>
                              Please select at least one option
                            </div>
                          )}
                        </FormGroup>
                      </div>
                    </ConditionalField>
                  );
                }
                
                // Renderização para o campo "Have a close relationship with someone who fits the profile above?"
                if (field.id === 'hasRelationshipWithPEP') {
                  return (
                    <FormGroup key={field.id}>
                      <Label htmlFor={field.id}>
                        {field.label} {field.required && <Obrigatorio>*</Obrigatorio>}
                      </Label>
                      <RadioGroup>
                        <RadioOption>
                          <Radio 
                            id="hasRelationshipWithPEPYes"
                            type="radio"
                            name="hasRelationshipWithPEP"
                            value="yes"
                            checked={formValues.hasRelationshipWithPEP === 'yes'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="hasRelationshipWithPEPYes">Yes</RadioLabel>
                        </RadioOption>
                        <RadioOption>
                          <Radio 
                            id="hasRelationshipWithPEPNo"
                            type="radio"
                            name="hasRelationshipWithPEP"
                            value="no"
                            checked={formValues.hasRelationshipWithPEP === 'no'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="hasRelationshipWithPEPNo">No</RadioLabel>
                        </RadioOption>
                      </RadioGroup>
                    </FormGroup>
                  );
                }
                
                // Renderização para os detalhes de relacionamento com PEP
                if (field.id === 'pepRelationshipDetails') {
                  return (
                    <ConditionalField key={field.id} visible={formValues.hasRelationshipWithPEP === 'yes'}>
                      <div style={{ marginBottom: '24px' }}>
                        <FormGroup>
                          <div style={{ textAlign: 'center', marginBottom: '12px', fontWeight: 'bold' }}>
                            <Obrigatorio>*</Obrigatorio> Select at least one option:
                          </div>
                          <div style={{ marginTop: '8px', marginLeft: '20%', width: '60%' }}>
                            <CheckboxWithLabel style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                              <Checkbox 
                                id="isSpousePartner"
                                type="checkbox"
                                name="isSpousePartner"
                                checked={formValues.isSpousePartner === 'yes'}
                                onChange={handleChange}
                                disabled={formValues.hasRelationshipWithPEP !== 'yes'}
                              />
                              <CheckboxLabel htmlFor="isSpousePartner" style={{ marginLeft: '10px' }}>
                                Spouse or partner
                              </CheckboxLabel>
                            </CheckboxWithLabel>
                            
                            <CheckboxWithLabel style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                              <Checkbox 
                                id="isFamily"
                                type="checkbox"
                                name="isFamily"
                                checked={formValues.isFamily === 'yes'}
                                onChange={handleChange}
                                disabled={formValues.hasRelationshipWithPEP !== 'yes'}
                              />
                              <CheckboxLabel htmlFor="isFamily" style={{ marginLeft: '10px' }}>
                                Parents, siblings, children
                              </CheckboxLabel>
                            </CheckboxWithLabel>
                            
                            <CheckboxWithLabel style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                              <Checkbox 
                                id="isBusinessAssociate"
                                type="checkbox"
                                name="isBusinessAssociate"
                                checked={formValues.isBusinessAssociate === 'yes'}
                                onChange={handleChange}
                                disabled={formValues.hasRelationshipWithPEP !== 'yes'}
                              />
                              <CheckboxLabel htmlFor="isBusinessAssociate" style={{ marginLeft: '10px' }}>
                                Business associates or close collaborators
                              </CheckboxLabel>
                            </CheckboxWithLabel>
                          </div>
                          {formValues.hasRelationshipWithPEP === 'yes' && 
                           !(formValues.isSpousePartner === 'yes' || 
                             formValues.isFamily === 'yes' || 
                             formValues.isBusinessAssociate === 'yes') && (
                            <div style={{ color: theme.error, marginTop: '8px', fontSize: '14px', textAlign: 'center' }}>
                              Please select at least one option
                            </div>
                          )}
                        </FormGroup>
                      </div>
                    </ConditionalField>
                  );
                }
                
                // Renderização para o campo "Has government contracts?"
                if (field.id === 'hasGovernmentContracts') {
                  return (
                    <FormGroup key={field.id}>
                      <Label htmlFor={field.id}>
                        {field.label} {field.required && <Obrigatorio>*</Obrigatorio>}
                      </Label>
                      <RadioGroup>
                        <RadioOption>
                          <Radio 
                            id="hasGovernmentContractsYes"
                            type="radio"
                            name="hasGovernmentContracts"
                            value="yes"
                            checked={formValues.hasGovernmentContracts === 'yes'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="hasGovernmentContractsYes">Yes</RadioLabel>
                        </RadioOption>
                        <RadioOption>
                          <Radio 
                            id="hasGovernmentContractsNo"
                            type="radio"
                            name="hasGovernmentContracts"
                            value="no"
                            checked={formValues.hasGovernmentContracts === 'no'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="hasGovernmentContractsNo">No</RadioLabel>
                        </RadioOption>
                      </RadioGroup>
                    </FormGroup>
                  );
                }
                
                // Renderização para os detalhes de contratos governamentais
                if (field.id === 'governmentContractDetails') {
                  return (
                    <ConditionalField key={field.id} visible={formValues.hasGovernmentContracts === 'yes'}>
                      <div style={{ marginBottom: '24px' }}>
                        <FormGroup>
                          <Label htmlFor="contractType">
                            Type of contract <Obrigatorio>*</Obrigatorio>
                          </Label>
                          <Input 
                            id="contractType"
                            type="text"
                            name="contractType"
                            value={formValues.contractType || ''}
                            onChange={handleChange}
                            disabled={formValues.hasGovernmentContracts !== 'yes'}
                            required={formValues.hasGovernmentContracts === 'yes'}
                          />
                        </FormGroup>
                        
                        <FormGroup style={{ marginTop: '16px' }}>
                          <Label htmlFor="publicEntityInvolved">
                            Public entity involved <Obrigatorio>*</Obrigatorio>
                          </Label>
                          <Input 
                            id="publicEntityInvolved"
                            type="text"
                            name="publicEntityInvolved"
                            value={formValues.publicEntityInvolved || ''}
                            onChange={handleChange}
                            disabled={formValues.hasGovernmentContracts !== 'yes'}
                            required={formValues.hasGovernmentContracts === 'yes'}
                          />
                        </FormGroup>
                        
                        <FormGroup style={{ marginTop: '16px' }}>
                          <Label htmlFor="customerRole">
                            Customer's role in the process <Obrigatorio>*</Obrigatorio>
                          </Label>
                          <Input 
                            id="customerRole"
                            type="text"
                            name="customerRole"
                            value={formValues.customerRole || ''}
                            onChange={handleChange}
                            disabled={formValues.hasGovernmentContracts !== 'yes'}
                            required={formValues.hasGovernmentContracts === 'yes'}
                          />
                        </FormGroup>
                      </div>
                    </ConditionalField>
                  );
                }
                
                // Renderização para o campo "Works with agrobusiness?"
                if (field.id === 'worksWithAgrobusiness') {
                  return (
                    <FormGroup key={field.id}>
                      <Label htmlFor={field.id}>
                        {field.label} {field.required && <Obrigatorio>*</Obrigatorio>}
                      </Label>
                      <RadioGroup>
                        <RadioOption>
                          <Radio 
                            id="worksWithAgrobusinessYes"
                            type="radio"
                            name="worksWithAgrobusiness"
                            value="yes"
                            checked={formValues.worksWithAgrobusiness === 'yes'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="worksWithAgrobusinessYes">Yes</RadioLabel>
                        </RadioOption>
                        <RadioOption>
                          <Radio 
                            id="worksWithAgrobusinessNo"
                            type="radio"
                            name="worksWithAgrobusiness"
                            value="no"
                            checked={formValues.worksWithAgrobusiness === 'no'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="worksWithAgrobusinessNo">No</RadioLabel>
                        </RadioOption>
                      </RadioGroup>
                    </FormGroup>
                  );
                }

                // Renderização para os detalhes de agronegócio
                if (field.id === 'agrobusinessDetails') {
                  return (
                    <ConditionalField key={field.id} visible={formValues.worksWithAgrobusiness === 'yes'}>
                      <div style={{ marginBottom: '24px' }}>
                        {/* O campo cropsProductsInvolved foi removido */}
                      </div>
                    </ConditionalField>
                  );
                }

                // Renderização para o campo "Has inherited assets"
                if (field.id === 'hasInheritedAssets') {
                  return (
                    <FormGroup key={field.id}>
                      <Label htmlFor={field.id}>
                        {field.label} {field.required && <Obrigatorio>*</Obrigatorio>}
                      </Label>
                      <RadioGroup>
                        <RadioOption>
                          <Radio 
                            id="hasInheritedAssetsYes"
                            type="radio"
                            name="hasInheritedAssets"
                            value="yes"
                            checked={formValues.hasInheritedAssets === 'yes'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="hasInheritedAssetsYes">Yes</RadioLabel>
                        </RadioOption>
                        <RadioOption>
                          <Radio 
                            id="hasInheritedAssetsNo"
                            type="radio"
                            name="hasInheritedAssets"
                            value="no"
                            checked={formValues.hasInheritedAssets === 'no'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="hasInheritedAssetsNo">No</RadioLabel>
                        </RadioOption>
                      </RadioGroup>
                    </FormGroup>
                  );
                }

                // Renderização para os detalhes de herança
                if (field.id === 'inheritanceDetails') {
                  return (
                    <ConditionalField key={field.id} visible={formValues.hasInheritedAssets === 'yes'}>
                      <div style={{ marginBottom: '24px' }}>
                        <FormGroup>
                          <Label htmlFor="valueInherited">
                            Value inherited <Obrigatorio>*</Obrigatorio>
                          </Label>
                          <Input 
                            id="valueInherited"
                            type="number"
                            name="valueInherited"
                            value={formValues.valueInherited || ''}
                            onChange={handleChange}
                            disabled={formValues.hasInheritedAssets !== 'yes'}
                            required={formValues.hasInheritedAssets === 'yes'}
                          />
                        </FormGroup>
                        
                        <FormGroup style={{ marginTop: '16px' }}>
                          <Label htmlFor="relationshipToDeceased">
                            Relationship to the deceased <Obrigatorio>*</Obrigatorio>
                          </Label>
                          <Input 
                            id="relationshipToDeceased"
                            type="text"
                            name="relationshipToDeceased"
                            value={formValues.relationshipToDeceased || ''}
                            onChange={handleChange}
                            disabled={formValues.hasInheritedAssets !== 'yes'}
                            required={formValues.hasInheritedAssets === 'yes'}
                          />
                        </FormGroup>
                        
                        <FormGroup style={{ marginTop: '16px' }}>
                          <Label htmlFor="nameOfDeceased">
                            Name of deceased (if &gt;20% of estate is liquid) <Obrigatorio>*</Obrigatorio>
                          </Label>
                          <Input 
                            id="nameOfDeceased"
                            type="text"
                            name="nameOfDeceased"
                            value={formValues.nameOfDeceased || ''}
                            onChange={handleChange}
                            disabled={formValues.hasInheritedAssets !== 'yes'}
                            required={formValues.hasInheritedAssets === 'yes'}
                          />
                        </FormGroup>
                      </div>
                    </ConditionalField>
                  );
                }
                
                // Renderização para o campo "Have you ever held a government position?"
                if (field.id === 'hasGovernmentPosition') {
                  return (
                    <FormGroup key={field.id}>
                      <Label htmlFor={field.id}>
                        {field.label} {field.required && <Obrigatorio>*</Obrigatorio>}
                      </Label>
                      <RadioGroup>
                        <RadioOption>
                          <Radio 
                            id="hasGovernmentPositionYes"
                            type="radio"
                            name="hasGovernmentPosition"
                            value="yes"
                            checked={formValues.hasGovernmentPosition === 'yes'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="hasGovernmentPositionYes">Yes</RadioLabel>
                        </RadioOption>
                        <RadioOption>
                          <Radio 
                            id="hasGovernmentPositionNo"
                            type="radio"
                            name="hasGovernmentPosition"
                            value="no"
                            checked={formValues.hasGovernmentPosition === 'no'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="hasGovernmentPositionNo">No</RadioLabel>
                        </RadioOption>
                      </RadioGroup>
                    </FormGroup>
                  );
                }
                
                // Renderização para os detalhes de cargo governamental
                if (field.id === 'governmentPositionDetails') {
                  return (
                    <ConditionalField key={field.id} visible={formValues.hasGovernmentPosition === 'yes'}>
                      <div style={{ marginBottom: '24px' }}>
                        <FormGroup>
                          <Label htmlFor="positionTitle">
                            Exact title of position <Obrigatorio>*</Obrigatorio>
                          </Label>
                          <Input 
                            id="positionTitle"
                            type="text"
                            name="positionTitle"
                            value={formValues.positionTitle || ''}
                            onChange={handleChange}
                            disabled={formValues.hasGovernmentPosition !== 'yes'}
                            required={formValues.hasGovernmentPosition === 'yes'}
                          />
                        </FormGroup>
                        
                        <FormGroup style={{ marginTop: '16px' }}>
                          <Label htmlFor="publicFigureName">
                            Name of the public figure (if relationship-based) <Obrigatorio>*</Obrigatorio>
                          </Label>
                          <Input 
                            id="publicFigureName"
                            type="text"
                            name="publicFigureName"
                            value={formValues.publicFigureName || ''}
                            onChange={handleChange}
                            disabled={formValues.hasGovernmentPosition !== 'yes'}
                            required={formValues.hasGovernmentPosition === 'yes'}
                          />
                        </FormGroup>
                        
                        <FormGroup style={{ marginTop: '16px' }}>
                          <Label htmlFor="organizationName">
                            Name of organization or entity <Obrigatorio>*</Obrigatorio>
                          </Label>
                          <Input 
                            id="organizationName"
                            type="text"
                            name="organizationName"
                            value={formValues.organizationName || ''}
                            onChange={handleChange}
                            disabled={formValues.hasGovernmentPosition !== 'yes'}
                            required={formValues.hasGovernmentPosition === 'yes'}
                          />
                        </FormGroup>
                        
                        <FormGroup style={{ marginTop: '16px' }}>
                          <Label htmlFor="countryJurisdiction">
                            Country and jurisdiction <Obrigatorio>*</Obrigatorio>
                          </Label>
                          <Input 
                            id="countryJurisdiction"
                            type="text"
                            name="countryJurisdiction"
                            value={formValues.countryJurisdiction || ''}
                            onChange={handleChange}
                            disabled={formValues.hasGovernmentPosition !== 'yes'}
                            required={formValues.hasGovernmentPosition === 'yes'}
                          />
                        </FormGroup>
                        
                        <FormGroup style={{ marginTop: '16px' }}>
                          <Label htmlFor="positionStartDate">
                            Start date <Obrigatorio>*</Obrigatorio>
                          </Label>
                          {formValues.hasGovernmentPosition === 'no' ? (
                            <Input 
                              id="positionStartDate"
                              type="hidden"
                              name="positionStartDate"
                              value="no"
                            />
                          ) : (
                            <Input 
                              id="positionStartDate"
                              type="date"
                              name="positionStartDate"
                              value={formValues.positionStartDate || ''}
                              onChange={handleChange}
                              disabled={formValues.hasGovernmentPosition !== 'yes'}
                              required={formValues.hasGovernmentPosition === 'yes'}
                            />
                          )}
                        </FormGroup>
                        
                        <FormGroup style={{ marginTop: '16px' }}>
                          <Label htmlFor="positionEndDate">
                            End date <Obrigatorio>*</Obrigatorio>
                          </Label>
                          {formValues.hasGovernmentPosition === 'no' ? (
                            <Input 
                              id="positionEndDate"
                              type="hidden"
                              name="positionEndDate"
                              value="no"
                            />
                          ) : (
                            <Input 
                              id="positionEndDate"
                              type="date"
                              name="positionEndDate"
                              value={formValues.positionEndDate || ''}
                              onChange={handleChange}
                              disabled={formValues.hasGovernmentPosition !== 'yes'}
                              required={formValues.hasGovernmentPosition === 'yes'}
                            />
                          )}
                        </FormGroup>
                      </div>
                    </ConditionalField>
                  );
                }
                
                // Renderização para o campo "Have you completed a college or university program?"
                if (field.id === 'hasCompletedCollege') {
                  return (
                    <FormGroup key={field.id}>
                      <Label htmlFor={field.id}>
                        {field.label} {field.required && <Obrigatorio>*</Obrigatorio>}
                      </Label>
                      <RadioGroup>
                        <RadioOption>
                          <Radio 
                            id="hasCompletedCollegeYes"
                            type="radio"
                            name="hasCompletedCollege"
                            value="yes"
                            checked={formValues.hasCompletedCollege === 'yes'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="hasCompletedCollegeYes">Yes</RadioLabel>
                        </RadioOption>
                        <RadioOption>
                          <Radio 
                            id="hasCompletedCollegeNo"
                            type="radio"
                            name="hasCompletedCollege"
                            value="no"
                            checked={formValues.hasCompletedCollege === 'no'}
                            onChange={handleChange}
                            required={field.required}
                          />
                          <RadioLabel htmlFor="hasCompletedCollegeNo">No</RadioLabel>
                        </RadioOption>
                      </RadioGroup>
                    </FormGroup>
                  );
                }
                
                // Renderização para os campos de educação (só aparecem se "Has completed college" for "yes")
                if ((field.id === 'institution' || field.id === 'degree' || field.id === 'fieldsOfStudy' || 
                     field.id === 'datesOfCompletion' || field.id === 'relevantContinuingEducation')) {
                  return (
                    <ConditionalField key={field.id} visible={formValues.hasCompletedCollege === 'yes'}>
                      <FormGroup>
                        <Label htmlFor={field.id}>
                          {field.label} {field.required && <Obrigatorio>*</Obrigatorio>}
                        </Label>
                        <Input 
                          id={field.id}
                          type={field.type}
                          name={field.id}
                          value={formValues[field.id] || ''}
                          onChange={handleChange}
                          disabled={formValues.hasCompletedCollege !== 'yes'}
                          required={field.required && formValues.hasCompletedCollege === 'yes'}
                        />
                      </FormGroup>
                    </ConditionalField>
                  );
                }
                
                // Renderização padrão para os outros campos
                return (
                <FormGroup key={field.id}>
                  <Label htmlFor={field.id}>
                    {field.label} {field.required && <Obrigatorio>*</Obrigatorio>}
                  </Label>
                  <Input 
                    id={field.id}
                    type={field.type}
                    name={field.id}
                    value={formValues[field.id] || ''}
                    onChange={handleChange}
                    required={field.required}
                  />
                </FormGroup>
                );
              })}
            </FormSection>

            {/* Botões de navegação */}
            <NavigationButtons>
              <PrevButton 
                type="button" 
                onClick={handlePrev} 
                disabled={currentSection === 0}
              >
                {renderIcon(FiArrowLeft, { size: 16 })} Previous
              </PrevButton>
              
              {currentSection < sections.length - 1 ? (
                <NextButton 
                  type="button" 
                  onClick={handleNext} 
                  disabled={false}
                >
                  Next {renderIcon(FiArrowRight, { size: 16 })}
                </NextButton>
              ) : (
                <>
                  <SubmitButton type="submit" onClick={handleSubmit} disabled={isSubmitting}>
                  {isSubmitting ? 'Enviando...' : 'Submit'}
                </SubmitButton>
                </>
              )}
            </NavigationButtons>
          </Form>
        </FormContainer>
      </TitleContainer>
      
      {/* Toast de erro */}
      {showErrorToast && (
        <ToastContainer type="error">
          {renderIcon(FiX, { size: 16, color: theme.primaryOn })}
          <span>{toastMessage}</span>
        </ToastContainer>
      )}
      
      {/* Toast de sucesso */}
      {showSuccessToast && (
        <ToastContainer type="success">
          {renderIcon(FiCheck, { size: 16, color: theme.primaryOn })}
          <span>{toastMessage}</span>
        </ToastContainer>
      )}

      {/* Componente de Loading */}
      {isLoading && <Loading />}
    </Container>
  );
};

export default FormPage; 